import { sortBy } from 'lodash-es'

import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { AIDraftStatus } from '@lyrahealth-inc/shared-app-logic/src/models/appointmentSummary/AppointmentSummary'
import { AIDraft, AIDraftPromptType } from '@lyrahealth-inc/shared-app-logic/src/models/draft/AIDraft'

import { mixpanelEvents } from '../../../../../../../../../mixpanel/mixpanelConstants'
import { track } from '../../../../../../../../../mixpanel/mixpanelTracking'
import { getClientFullName } from '../../../../../../../common/utils/utils'
import { useAppDispatch } from '../../../../../../../data/store'
import { useAllClientsData } from '../../../../../../clients/data/hooks/useAllClientsData'
import { setInsertSmartSuggestionForClientId } from '../../../../../data/messagesSlice'

export default function useMapAIDraftsToUtilityItems(
  aiDrafts: AIDraft[],
  onConversationPressed: ({ clientId, externallySelected }: { clientId: string; externallySelected?: boolean }) => void,
  navigateToAssignment: (clientId: string, assignmentId: string) => void,
  navigateToNote: (clientId: string, noteId: string) => void,
  setAIDraftToExpire: (draft: AIDraft) => void,
) {
  const { isPreferredNameEnabled } = useFlags()
  const [clients] = useAllClientsData()
  const dispatch = useAppDispatch()
  const drafts =
    aiDrafts
      ?.filter((draft) => draft.status === AIDraftStatus.COMPLETE)
      .map((draft) => {
        const client = clients.find((client) => client.id === draft.patient_id)
        const clientName = client ? getClientFullName(client, isPreferredNameEnabled) : ''
        return {
          id: draft.patient_id,
          text: draft.summary_text,
          header: clientName,
          onDeletePress: () => {
            track({
              event: mixpanelEvents.DELETE_SMART_SUGGESTION_ICON,
              properties: {
                gen_ai_submission_tracker_id: draft.id,
                prompt_type: draft.prompt_type,
              },
            })
            setAIDraftToExpire(draft)
          },
          onEditPress: () => {
            track({
              event: mixpanelEvents.EDIT_SMART_SUGGESTION_ICON,
              properties: {
                gen_ai_submission_tracker_id: draft.id,
                prompt_type: draft.prompt_type,
              },
            })
            dispatch(setInsertSmartSuggestionForClientId(draft.patient_id))
            onConversationPressed({ clientId: draft.patient_id })
          },
          onPress: () => {
            track({
              event: mixpanelEvents.OPEN_CONVERSATION_FROM_SMART_SUGGESTION,
              properties: {
                gen_ai_submission_tracker_id: draft.id,
                prompt_type: draft.prompt_type,
              },
            })
            onConversationPressed({ clientId: draft.patient_id })
          },
          onOpenOriginalFormPress: () => {
            track({
              event: mixpanelEvents.OPEN_ORIGINAL_FORM_FROM_SMART_SUGGESTION,
              properties: {
                gen_ai_submission_tracker_id: draft.id,
                prompt_type: draft.prompt_type,
              },
            })
            if (draft.prompt_type === AIDraftPromptType.INTAKE) {
              navigateToAssignment(draft.patient_id, draft.assignment_id)
            } else {
              navigateToNote(draft.patient_id, draft.note_id)
            }
          },
          updateDate: draft.create_date,
          useHoverActionButtons: true,
        }
      }) ?? []

  return sortBy(drafts, (draft) => new Date(draft.updateDate).getTime() * -1)
}
